import { defineStore } from 'pinia';
import { useBranchStore } from '@/Stores/branch';
import { useBannersStore } from '@/Stores/banners';
import { useNormalOrderStore } from '@/Stores/normalorder';
import { useNotificationsStore } from '@/Stores/notifications';
import { useAffinityOrderStore } from '@/Stores/affinityorder';
import { axios } from '@/Services/Backend';
import { setLocale, DEFAULT_LOCALE } from '@/i18n';

export const useUserStore = defineStore('user', {
    state: () => ({
        user: null,
        isFreshLogin: false,
        branchCount: 0,

        tawkChat: null,
        tawkChatHash: null,
        validIps:['62.28.161.46','78.137.194.77','192.168.']
    }),

    actions: {
        async login(router, tawkChat,admin) {
            this.isFreshLogin = true;

            // Since we can't guarantee that users will logout by pressing the logout button (session can expire, etc.), we clear these stores both here and on the logout button
            useBranchStore().setBranch(null);
            useBannersStore().setShowPopupBillboards(true);

            await this.getUser();

            // Set user attributes to Tawk Chat
            this.tawkChat = tawkChat;
            this.getTawkChatHash();

            if(admin){
                router.visit('/backoffice');
            }else{
                router.visit('dashboard');
            }

        },

        logout(inertia, tawkChat, preventRedirect = false) {
            const userId = this.user?.id;
            const sellerId = this.user?.seller_id;

            axios.post('/logout', { preventRedirect: preventRedirect }).then((response) => {
                this.tawkChat = tawkChat;

                // End ongoing TawkChat
                if (this.tawkChat?.isChatOngoing()) {
                    this.tawkChat.endChat();
                }

                this.$reset();
                setLocale(DEFAULT_LOCALE);
                useNotificationsStore().logout(userId, useBranchStore().branch?.id, sellerId);
                useNotificationsStore().logout(userId);
                useBranchStore().setBranch(null);
                useBannersStore().setShowPopupBillboards(true);

                if (response.data == 'redirect') inertia.visit('/');
            });
        },

        async getTawkChatHash() {
            axios
                .get(`/api/user/tawk-chat-hash/${this.user.email}`)
                .then((response) => {
                    this.tawkChatHash = response.data.hash ?? null;
                    this.tawkChatHash ? this.setTawkChatAttributes() : console.log('Unable to set visitor TawkChat values. Unknown hash.');
                })
                .catch((error) => console.error(error));
        },

        async refreshUser() {
            return axios
                .get('/api/user/introspect')
                .then(({ data }) => {
                    this.user = data.user;
                    this.branchCount = data.branchCount;
                    setLocale(this.user?.locale);
                })
                .catch((error) => console.error(error));
        },

        async getUser() {
            if (this.user) return;

            const response = await axios.get('/api/user/introspect');
            this.user = response.data.user;
            this.branchCount = response.data.branchCount;
            setLocale(this.user?.locale);

            // Join the websockets' channels and retrieve the latest notifications for this user
            useNotificationsStore().initialize();

            // This is here to prevent a bug in which the localStorage was being read before the user being loaded.
            useNormalOrderStore().reloadFromLocalStorage();
            useAffinityOrderStore().reloadFromLocalStorage();

            this.hasPermissionTo('Copy website data') ? document.removeEventListener('copy', this.forbidCopying) : document.addEventListener('copy', this.forbidCopying);
        },

        forbidCopying(event) {
            event.clipboardData.setData('text/plain', '');
            event.preventDefault();
        },

        setInactiveFreshLogin() {
            this.isFreshLogin = false;
        },

        setTawkChatAttributes() {
            this.tawkChat?.setAttributes(
                {
                    name: this.user.name,
                    email: this.user.email,
                    hash: this.tawkChatHash,
                },
                function (error) {
                    console.log('TawkChat set attributes failed.', error);
                }
            );

            if (this.tawkChat) {
                this.tawkChat.visitor = {
                    id: this.user.id,
                    name: this.user.name,
                    email: this.user.email,
                    hash: this.tawkChatHash,
                };
            }
        },
    },

    getters: {
        isLoggedIn: (state) => !!state.user,
        hasPermissionTo: (state) => (permission) => state.user?.permissions?.includes(permission) ?? false,
    },
});
